import { BreakpointObserver } from "@angular/cdk/layout";
import { StepperSelectionEvent } from "@angular/cdk/stepper";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from "@angular/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";
import { MatStepper } from "@angular/material/stepper";
import { CellularDevice, CellularProviderStatuses } from "@app/cellular/models/cellular-device.model";
import { CellularService } from "@app/cellular/services/cellular.service";
import { Customer } from "@app/customer/model/customer";
import { Entity } from "@app/entity/model/entity";
import { CHSecurityDevice, ClareServicePlan, ClareServiceSubscription, ClareServiceSubscriptionHelper, Project } from "@app/project/model";
import { UIAdditionalFeature } from "@app/project/model/project-feature";
import { SERVICE_PLAN_DIALOG_VIEW, ServicePlanTiers } from "@app/project/model/service-plan";
import { CHServiceService } from "@app/project/services/ch-services.service";
import { ProjectHelperService } from "@app/project/services/project-helper.service";
import { ProjectStatusService } from "@app/project/services/project-status.service";
import { ProjectService } from "@app/project/services/project.service";
import { ServicePlansService } from "@app/project/services/service-plans.service";
import { RouterActions } from "@app/root-store";
import { ClareStatuses } from "@app/shared/model/clare-status.model";
import { NgChanges } from "@app/shared/model/ng-changes.model";
import { activeStep, ClareProcessStep, getStepByTag } from "@app/shared/model/utility.model";
import { EquatorClareHomeService } from "@app/shared/services/equator-api/equator-clarehome/equator-clarehome.service";
import { finalize, map, of, ReplaySubject, Subject, switchMap, take, takeUntil, tap } from "rxjs";
import { ServicePlanOptionsComponent } from "../security/service-plan-options/service-plan-options.component";

@Component({
    selector: "app-activate-account",
    templateUrl: "./activate-account.component.html",
    styleUrls: ["./activate-account.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivateAccountComponent implements OnChanges, OnInit, OnDestroy {
    @ViewChild("activeAccountStepper") activeAccountStepper: MatStepper;
    @ViewChild("comparePlanBox", { static: true }) comparePlanBox: TemplateRef<ServicePlanOptionsComponent>;
    @Input() selectedPlan: ClareServicePlan;
    @Input() subscription: ClareServiceSubscription;
    @Input() project: Project;
    @Input() cellularDevice: CellularDevice;
    @Input() activeView = SERVICE_PLAN_DIALOG_VIEW.SELECT_PLAN;
    @Input() isProjectOnline = false;
    @Input() customer: Customer;

    @Input() entity: Entity;
    @Input() isServicePlanSelected = false;
    @Output() updateDone = new EventEmitter<ClareServicePlan>();
    @Output() navigateEditUser = new EventEmitter();

    chSecurityDevice$ = new ReplaySubject<CHSecurityDevice | undefined>(1);
    uuid = "";
    isLteSupported = false;
    isClareOne = false;
    isCheckedClare = false;
    isLteDisabled = false;
    isCellularTriggering = false;
    compareView = SERVICE_PLAN_DIALOG_VIEW.COMPARE_PLANS;
    updatedFeature: { selectedPlan: ClareServicePlan; featureObj: UIAdditionalFeature[] } = { selectedPlan: null, featureObj: [] };
    isMobile = true; //should only display the compare button if it's truly not mobile device
    isProjectOffline = false;
    isStepperLoaded = true;
    ignoreMonitoringService = false;

    servicePlanStepIndex = 0;
    skipWaitingActivateLTE = false;
    private readonly _servicePlanStepTag = "service_plan_feature";
    reviewContactStepIndex = -1;
    private readonly _reviewContactStepTag = "review_contact_info";
    configureMSStepIndex = -1;
    private readonly _configureMSStepTag = "config_ms";
    activateLTEStepIndex = -1;
    private readonly _activateLTEStepTag = "active_lte";

    private readonly servicePlanStep: ClareProcessStep = {
        id: 0,
        tag: this._servicePlanStepTag,
        text: "Service Plan & Features",
        isActive: true,
        isCompleted: false,
    };

    private readonly reviewContactStep: ClareProcessStep = {
        id: 1,
        tag: this._reviewContactStepTag,
        text: "Review Contact Info for Monitoring Services",
        isActive: false,
        isCompleted: false,
    };

    private readonly configureMSStep: ClareProcessStep = {
        id: 2,
        tag: this._configureMSStepTag,
        text: "Configure Monitoring Services",
        isActive: false,
        isCompleted: false,
    };

    private readonly activateLTEStep: ClareProcessStep = {
        id: 3,
        tag: this._activateLTEStepTag,
        text: "Activate LTE",
        isActive: false,
        isCompleted: false,
    };

    private currentStepIndex = 0;
    private isFetchingChSecurityDevice = false;
    private isFetchingCellularDevice = false;
    private unsubscribe$ = new Subject();
    private isSubcriptionAdded = false;

    steps = [this.servicePlanStep];

    constructor(
        private servicePlanService: ServicePlansService,
        private readonly equatorClareHomeService: EquatorClareHomeService,
        private readonly cellularService: CellularService,
        private readonly chServiceService: CHServiceService,
        private readonly cdr: ChangeDetectorRef,
        private readonly breakpointObserver$: BreakpointObserver,
        private readonly projectStatusService: ProjectStatusService,
        private readonly projectService: ProjectService,
        private readonly dialog: MatDialog
    ) {}

    ngOnChanges(changes: NgChanges<ActivateAccountComponent>): void {
        if (changes.project?.currentValue) {
            const uuid = ProjectHelperService.getProjectUuid(changes.project.currentValue);
            if (uuid && this.uuid !== uuid) {
                this.uuid = uuid;
                this.checkIsClareone(this.uuid);
                this.fetchChSecurityDevice(this.uuid);
                this.fetchCellularDevice();
            }
        }

        if (changes.selectedPlan || changes.subscription) {
            const selectedPlan = changes.selectedPlan?.currentValue || this.selectedPlan;
            const subscription = changes.subscription?.currentValue || this.subscription;
            this.reloadServicePlan(subscription, selectedPlan);
        }

        if (changes.selectedPlan?.currentValue) {
            this.onFieldsChange({
                selectedPlan: changes.selectedPlan?.currentValue,
                featureObj: this.updatedFeature?.featureObj || [],
            });
        }

        if (changes.subscription?.currentValue) {
            this.checkIfSubscriptionExisting(changes.subscription.currentValue);
        }
        //Allow to process next step if LTE was activated.
        this.skipWaitingActivateLTE = this.isEnableLteStepComplete;
    }

    private checkIfSubscriptionExisting(subscription: ClareServiceSubscription) {
        if (subscription?.id) {
            const currentStep = getStepByTag(this.steps, this._servicePlanStepTag);
            if (currentStep) {
                currentStep.isCompleted = true;
                if (this.isSubcriptionAdded) {
                    this.manualTriggerNext(1);
                    this.isSubcriptionAdded = false;
                }
            }
        }
    }

    ngOnInit(): void {
        this.breakpointObserver$
            .observe(["(max-width: 959px)"])
            .pipe(
                map(mobileQuery => mobileQuery.matches),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(isMobile => {
                this.isMobile = isMobile;
                this.cdr.markForCheck();
            });
        this.projectStatusService
            .getActiveProjectStatus$()
            .pipe(
                takeUntil(this.unsubscribe$),
                tap(status => {
                    this.isProjectOffline = status[0] === ClareStatuses.Offline;
                })
            )
            .subscribe();
        this.fetchCellularDevice();
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    cancel() {
        if (!this.subscription) {
            this.projectService.dispatch(RouterActions.routerGo({ path: ["/accounts"] }));
            return;
        }

        this.finishWizard();
    }

    onFetchCellDevice() {
        this.fetchCellularDevice();
    }

    fetchChSecurityDevice(uuid) {
        if (this.isFetchingChSecurityDevice) {
            return;
        }

        of(null)
            .pipe(
                tap(() => (this.isFetchingChSecurityDevice = true)),

                switchMap(() => this.getSecurityDevice$(uuid)),
                tap(device => this.chSecurityDevice$.next(device)),
                take(1),
                finalize(() => (this.isFetchingChSecurityDevice = false))
            )
            .subscribe();
    }

    private getSecurityDevice$(uuid) {
        return this.chServiceService.getSecurityPanel(uuid).pipe(map(securityDevices => securityDevices?.[0]));
    }

    checkIsClareone(uuid: string) {
        this.isCheckedClare = false;
        this.equatorClareHomeService
            .getServerInfo(uuid)
            .pipe(take(1))
            .subscribe(serverInfo => {
                this.isClareOne = serverInfo.hardwarePlatform === "ClareOne";
                this.isCheckedClare = true;
            });
    }

    finishWizard() {
        this.isCellularTriggering = false;
        this.updateDone.emit(this.selectedPlan);
    }

    finishReviewContactSteps(reviewContactStepIndex: number) {
        this.manualTriggerNext(reviewContactStepIndex + 1);
    }

    onSaveSPAndNext() {
        const { featureObj, selectedPlan } = this.updatedFeature;
        const emitter = new EventEmitter();
        this.isSubcriptionAdded = true;
        emitter
            .pipe(
                take(1),
                tap(d => {
                    if (!!d) {
                        this.servicePlanService.updateStoredSubscription(d);
                        this.selectedPlan = selectedPlan || this.selectedPlan;
                    }
                })
            )
            .subscribe();
        this.servicePlanService.savePlans(
            this.subscription,
            this.selectedPlan,
            selectedPlan || this.selectedPlan,
            this.project,
            null,
            emitter,
            featureObj || [],
            false,
            this.isProjectOffline
        );
    }

    onFieldsChange(changes: { selectedPlan: ClareServicePlan; featureObj: UIAdditionalFeature[] }, isDialogClosed = false) {
        this.updatedFeature = {
            selectedPlan: changes.selectedPlan,
            featureObj: changes.featureObj?.filter(f => f.enable_new_feature) || [],
        };

        if (isDialogClosed) {
            this.dialog.closeAll();
        }
        this.updateWizardSteps(changes.selectedPlan);
        this.updateStepIndex();
        this.cdr.markForCheck();
    }

    private updateWizardSteps(selectedPlan: ClareServicePlan) {
        if (!selectedPlan) {
            return;
        }
        if (selectedPlan.name === ServicePlanTiers.INTERACTIVE_PLUS) {
            this.steps = [{ ...this.servicePlanStep }, { ...this.reviewContactStep }, { ...this.configureMSStep }];
            return;
        }

        if (selectedPlan.name === ServicePlanTiers.INTERACTIVE_PLUS_LTE) {
            this.steps = [
                { ...this.servicePlanStep },
                { ...this.activateLTEStep },
                { ...this.reviewContactStep },
                { ...this.configureMSStep },
            ];
            return;
        }

        this.steps = [{ ...this.servicePlanStep }];
    }

    onTabSelectionChange(tabChange: StepperSelectionEvent, previousIndex = -1) {
        this.currentStepIndex = tabChange.selectedIndex;
        activeStep(this.steps, this.currentStepIndex, previousIndex);
    }

    onEnableCellularTrigger() {
        this.isCellularTriggering = true;
    }
    inProcessingActivate() {
        this.skipWaitingActivateLTE = true;
    }

    onNavigateEditUser() {
        this.navigateEditUser.emit();
    }

    private updateStepIndex() {
        this.resetStepIndex();
        this.steps.forEach((step, index) => {
            if (step.tag === this._servicePlanStepTag) {
                this.servicePlanStepIndex = index;
            }

            if (step.tag === this._reviewContactStepTag) {
                this.reviewContactStepIndex = index;
            }

            if (step.tag === this._configureMSStepTag) {
                this.configureMSStepIndex = index;
            }

            if (step.tag === this._activateLTEStepTag) {
                this.activateLTEStepIndex = index;
            }
        });
    }

    resetStepIndex() {
        this.reviewContactStepIndex = -1;
        this.configureMSStepIndex = -1;
        this.activateLTEStepIndex = -1;
    }

    private reloadServicePlan(subscription: ClareServiceSubscription, servicePlan: ClareServicePlan): void {
        if (subscription) {
            this.isLteSupported = ClareServiceSubscriptionHelper.isFeatureEnabled(servicePlan, "cellular");
        } else {
            this.isLteSupported = false;
        }
    }

    private manualTriggerNext(nextIndex: number): void {
        // finish the wizard if the last step
        if (nextIndex >= this.steps.length) {
            this.finishWizard();
            return;
        }
        activeStep(this.steps, nextIndex, nextIndex - 1);
        this.cdr.detectChanges();

        setTimeout(() => {
            this.activeAccountStepper.next();
        }, 200);
    }

    private fetchCellularDevice() {
        if (this.isFetchingCellularDevice === true) {
            return;
        }

        this.isFetchingCellularDevice = true;

        const dbSecurityDevice = ProjectHelperService.getDbSecuritySystem(this.project);
        this.chSecurityDevice$
            .pipe(
                take(1),
                map(chSecurityDevice => {
                    // Create default device from existing data
                    this.cellularDevice = this.cellularService.getCellularDeviceProperties(chSecurityDevice, dbSecurityDevice);

                    return this.cellularDevice;
                }),
                switchMap(cellularDevice => {
                    const isBmobile = this.cellularService.isBmobile(dbSecurityDevice.cellularProvider);
                    if (isBmobile) {
                        return of(null);
                    }

                    return this.cellularService.fetchCellularDevice(cellularDevice.iccid, cellularDevice.provider);
                }),
                tap(fetchedDevice => {
                    if (fetchedDevice) {
                        this.cellularDevice = fetchedDevice;
                    }

                    this.isLteDisabled = this.checkLTEDisabled();
                }),
                finalize(() => {
                    this.isFetchingCellularDevice = false;
                })
            )
            .subscribe();
    }

    private checkLTEDisabled() {
        switch (this.cellularDevice.status) {
            case CellularProviderStatuses.Active:
                return false;
            case CellularProviderStatuses.Inactive:
            case CellularProviderStatuses.Deactive:
            case CellularProviderStatuses.PreActive:
            case CellularProviderStatuses.Unknown:
                return true;
            default:
                return null;
        }
    }

    get isEnableLteStepComplete(): boolean {
        const isPanelConfigured = this.project.securitysystem?.isCompleted;
        return !this.isLteSupported || (this.isLteDisabled === false && isPanelConfigured);
    }

    onUpdateFailed() {
        this.isCellularTriggering = false;
    }

    finishEnableLTE() {
        this.isCellularTriggering = false;
        const activateLTEStepIndex = this.steps.findIndex(step => step.tag === this._activateLTEStepTag);
        this.manualTriggerNext(activateLTEStepIndex + 1);
    }

    onComparePlans(event) {
        event.preventDefault();
        event.stopPropagation();
        this.dialog.open(this.comparePlanBox);
    }
    toggleSetupMonitoringService(event: MatCheckboxChange) {
        this.ignoreMonitoringService = event?.checked;
    }
}
